import onecolor from "onecolor";

import AppConstants from "../constants/AppConstants";

const { BLACK, DEFAULT_OPACITY, TRANSPARENT, PARSE_INT_DEFAULT_RADIX } = AppConstants;

/**
 * An object containing the two color parts normally used around the colorpicker
 * @typedef {object} ColorParts
 * @property {string} color - Either a hex value or color palette color
 * @property {number=} alpha - Value between 0 - 100 representing the color's alpha value
 */

/**
 * Returns a {r,g,b} object parsed from a hex string with or without the leading #
 * @param {string} hex
 * @returns {object}
 */

export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  const parseEntry = (index) => (result ? parseInt(result[index], 16) : 0);

  return {
    r: parseEntry(1),
    g: parseEntry(2),
    b: parseEntry(3),
  };
};

/**
 *  Returns a constructed color string from ColorParts
 * @param {ColorParts} colorParts
 * @returns {string}
 */
export const getStringFromColorParts = ({ color, alpha } = { color: null }) =>
  color === null ? TRANSPARENT : color + (alpha !== DEFAULT_OPACITY ? `:${alpha}` : "");

/**
 * Returns a ColorParts object parsed from a color string
 * @param {string} colorStr
 * @returns {ColorParts}
 */
export const getColorPartsFromString = (colorStr) => {
  let color = null;
  let alpha = DEFAULT_OPACITY;

  if (colorStr && colorStr.length > 1) {
    const parts = colorStr.split(":");
    [color] = parts;
    alpha = parts.length > 1 ? parseInt(parts[1], PARSE_INT_DEFAULT_RADIX) : DEFAULT_OPACITY;
  }

  return { color, alpha };
};

/**
 * Returns true if value is equal to black with 0 alpha. Can take in ColorParts or a string.
 * @param {*} value
 * @returns {boolean}
 */
export const isTransparent = (value) => {
  const { color, alpha } = typeof value === "string" ? getColorPartsFromString(value) : value;
  return color === BLACK && alpha === 0;
};

/**
 * Creates a palette color string from a color update object if a paletteIndex is > -1 is destructured from it.
 * Otherwise will return the original color;
 * @param {string} color
 * @param {object} updateInfo
 * @returns {string}
 */
export const getPaletteColorFromUpdate = (
  color,
  { paletteIndex, alpha = DEFAULT_OPACITY } = {}
) => {
  if (paletteIndex >= 0) {
    const basePaletteColor = `colors-${paletteIndex}`;
    return alpha !== DEFAULT_OPACITY ? `${basePaletteColor}:${alpha}` : basePaletteColor;
  }
  return color;
};

export const ColorParts = Object.freeze({
  fromString: getColorPartsFromString,
  toString: getStringFromColorParts,
  isTransparent,
});

// Function to calculate relative luminance using RGB
export const relativeLuminance = (color) => {
  const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : (c + 0.055) / 1.055 ** 2.4);

  const r = toLinear(color.red());
  const g = toLinear(color.green());
  const b = toLinear(color.blue());
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Function to calculate contrast ratio
export const contrastRatio = (l1, l2) => {
  const [lighter, darker] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Finds the pair of colors with the highest contrast ratio from an array of colors
 * @param {string[]} colors - Array of hex color strings to compare
 * @returns {Object} Object containing:
 *   - pair {string[]} Array of the two most contrasting hex colors
 *   - maxContrast {number} The contrast ratio between the two colors
 */
export const findMostContrastedColors = (colors) => {
  let maxContrast = 0;
  let pair = [];
  const luminances = colors
    .filter((hex) => !hex.includes(":"))
    .map((hex) => relativeLuminance(onecolor(hex)));
  
  for (let i = 0; i < colors.length; i++) {
    for (let j = i + 1; j < colors.length; j++) {
      const contrast = contrastRatio(luminances[i], luminances[j]);
      if (contrast > maxContrast) {
        maxContrast = contrast;
        pair = [i, j];
      }
    }
  }

  return { pair, maxContrast };
};
